import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBackHandler } from '../../hooks/useBackHandler';
import { SHEET_MS } from '../../hooks/useSheetTransition';

interface Props {
  /** Called (after the exit animation) when dismissed via backdrop / native back. */
  onDismiss: () => void;
  /** Receives `close(action)`, which animates out then runs `action`. Wire it to
   *  the X / Cancel / Confirm buttons so they animate too. */
  children: (close: (action: () => void) => void) => ReactNode;
  z?: number;
}

/** Smooth centered dialog — dimmed backdrop that fades and a card that scales in
 *  and out. All dismiss paths animate before unmounting. */
export function Dialog({ onDismiss, children, z = 70 }: Props) {
  const [visible, setVisible] = useState(false);
  const closing = useRef(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Animate out, then run the given action (backdrop/back use onDismiss).
  const run = (action: () => void) => {
    if (closing.current) return;
    closing.current = true;
    setVisible(false);
    window.setTimeout(action, SHEET_MS);
  };

  useBackHandler(true, () => run(onDismiss));

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center px-8" style={{ zIndex: z }}>
      <div
        onClick={() => run(onDismiss)}
        className={`absolute inset-0 bg-black/[0.32] transition-opacity ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDuration: `${SHEET_MS}ms` }}
      />
      <div
        className={`relative transition-[transform,opacity] ease-out ${
          visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{ transitionDuration: `${SHEET_MS}ms` }}
      >
        {children(run)}
      </div>
    </div>,
    document.body,
  );
}
